import React, { Fragment, useEffect, useState, forwardRef } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Snackbar,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
  Chip,
  MenuItem,
  useTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshingIndicator from "./RefreshingIndicator";
import SearchIcon from "@mui/icons-material/Search";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MemberDetail from "./MemberDetail";
import UserService from "../services/UserService";
import ManageCard from "./ManageCard";
import MemberTransactions from "./MemberTransactions";
import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import Labels from "../labels/Labels";
import NoteAddIcon from "@mui/icons-material/NoteAdd";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

export default function CompanyMembers(props) {
  const {
    isLoading,
    isFetching,
    isError,
    data,
    setPageFunction,
    setFilterStringFunction,
    dataCount,
    isLoadingDataCount,
    title,
    pageSize,
    onPageSizeChange,
    setLocationIdsFunction,
    dataLocations,
    isLoadingLocations,
  } = props;

  const [locationIds, setLocationIds] = React.useState({});
  const [elements, setElements] = React.useState([]);

  const theme = useTheme();

  // Function to fetch employer attributes
  async function fetchEmployerAttribute(attributeName) {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/attributes/${attributeName}`
    );
    return response;
  }

  // Query to fetch employer attribute for hiding routing/account fields
  const { data: hideRoutingFeature } = useQuery(
    ["hideRoutingFeature"],
    () => fetchEmployerAttribute("FEATURE_DISABLED_SHOW_ROUTING"),
    {
      select: (response) => response?.data?.value === "true",
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Log the hideRoutingFeature value for debugging
  console.log("hideRoutingFeature value:", hideRoutingFeature);

  const handleChangeElements = (event) => {
    const {
      target: { value },
    } = event;
    setLocationIdsFunction(value);
    setLocationIds(value);
    //refetchFunction();
    setElements(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const onFilterChange = (filter, params) => {
    const filterString = filter.quickFilterValues.join(" ");
    if (filterString === undefined) {
      setFilterStringFunction("");
    } else {
      setFilterStringFunction(filterString);
    }
    return filter;
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const [openNewDlg, setOpenNewDlg] = useState(false);
  const [openDetailDlg, setOpenDetailDlg] = useState(false);
  const [openMemberInfoDlg, setOpenMemberInfoDlg] = useState(false);

  const [memberInfo, setMemberInfo] = useState({});

  const onViewButtonClick = (e, params) => {
    setMemberInfo(params);
    setOpenNewDlg(true);
  };

  const onDetailButtonClick = (e, params) => {
    setMemberInfo(params);
    setOpenDetailDlg(true);
  };

  const onViewMemberInfoClick = (e, params) => {
    setMemberInfo(params);
    setOpenMemberInfoDlg(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataColumns = [
    { field: "id", headerName: "ID", width: 90, hidden: true },
    {
      field: "memberId",
      headerName: Labels.getLabel(
        "companyMembers.column.memberId",
        "Member Id"
      ),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: "1rem", mb: 0 }} gutterBottom>
            {params.row.memberId}
          </Typography>
        );
      },
    },
    // Conditionally include account number column (show by default unless feature is explicitly disabled)
    ...(hideRoutingFeature !== true
      ? [
          {
            field: "accountNumber",
            headerName: Labels.getLabel(
              "companyMembers.column.accountNumber",
              "Account #"
            ),
            width: 170,
            renderCell: (params) => {
              return (
                <Typography sx={{ fontSize: "1rem", mb: 0 }} gutterBottom>
                  {params.row.accountNumber}
                </Typography>
              );
            },
          },
        ]
      : []),
    // Conditionally include routing number column (show by default unless feature is explicitly disabled)
    ...(hideRoutingFeature !== true
      ? [
          {
            field: "routingNumber",
            headerName: Labels.getLabel(
              "companyMembers.column.routingNumber",
              "Routing"
            ),
            width: 75,
            renderCell: (params) => {
              return (
                <Typography sx={{ fontSize: "0.7rem", mb: 0 }} gutterBottom>
                  {params.row.routingNumber}
                </Typography>
              );
            },
          },
        ]
      : []),
    {
      field: "name",
      headerName: Labels.getLabel("companyMembers.column.name", "Name"),
      width: 170,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: "0.9rem", mb: 0 }} gutterBottom>
            {params.row.name}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: Labels.getLabel("companyMembers.column.email", "Email"),
      width: 250,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: "0.9rem", mb: 0 }} gutterBottom>
            {params?.row?.email?.includes("noemail")
              ? "NO EMAIL"
              : params.row.email}
          </Typography>
        );
      },
    },

    {
      field: "createdAt",
      headerName: Labels.getLabel(
        "companyMembers.column.createdAt",
        "Created At"
      ),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.createdAt}
          </Typography>
        );
      },
    },

    {
      field: "cardType",
      headerName: Labels.getLabel("companyMembers.column.cardType", "Card"),
      width: 90,
      renderCell: (params) => {
        return (
          <Typography
            sx={{ fontWeight: "bold", fontSize: "0.7rem", mb: 0 }}
            gutterBottom
          >
            {params.row.cardType}
          </Typography>
        );
      },
    },
    {
      field: "cardRequestStatus",
      headerName: Labels.getLabel(
        "companyMembers.column.enrollment",
        "Enrollment"
      ),
      width: 120,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.cardRequestStatus}
            </Typography>
            <Typography
              sx={{ fontSize: 8, mb: 0, fontWeight: "bold", color: "gray" }}
              gutterBottom
            >
              {params.row.customerStatus}
            </Typography>
          </div>
        );
      },
    },
    ...(UserService.hasRole([
      "pc-lock-unlock-cards",
      "pc-view-trx-cards",
      "pc-view-card-balance",
      "pc-tag-external-notes",
      "pad-view-info",
    ])
      ? [
          {
            field: "actions",
            headerName: Labels.getLabel(
              "companyMembers.column.actions",
              "Actions"
            ),
            width: 200,
            renderCell: (params) => {
              return (
                <>
                  <Tooltip
                    title={Labels.getLabel(
                      "companyMembers.tooltip.viewMemberInfo",
                      "View PAD info"
                    )}
                  >
                    <Button
                      style={{ minWidth: "10px" }}
                      color="primary"
                      onClick={(e) => onViewMemberInfoClick(e, params.row)}
                      sx={{
                        display:
                          UserService.hasRole(["pad-view-info"]) &&
                          (params.row.cardRequestStatus === "COMPLETED" ||
                            params.row.cardRequestStatus === "REQUESTED")
                            ? "block"
                            : "none",
                      }}
                    >
                      <VisibilityIcon />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={Labels.getLabel(
                      "companyMembers.tooltip.cardActions",
                      "Card Actions"
                    )}
                  >
                    <Button
                      sx={{
                        display:
                          UserService.hasRole([
                            "pc-lock-unlock-cards",
                            "pc-view-card-balance",
                          ]) &&
                          (params.row.cardRequestStatus === "COMPLETED" ||
                            params.row.cardRequestStatus === "REQUESTED")
                            ? "block"
                            : "none",
                      }}
                      color="primary"
                      onClick={(e) => onViewButtonClick(e, params.row)}
                    >
                      <CreditCardIcon />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={Labels.getLabel(
                      "companyMembers.tooltip.viewTransactions",
                      "View Transactions"
                    )}
                  >
                    <Button
                      sx={{
                        display:
                          UserService.hasRole(["pc-view-trx-cards"]) &&
                          (params.row.cardRequestStatus === "COMPLETED" ||
                            params.row.cardRequestStatus === "REQUESTED")
                            ? "block"
                            : "none",
                      }}
                      color="primary"
                      onClick={(e) => onDetailButtonClick(e, params.row)}
                    >
                      <ReadMoreIcon />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={Labels.getLabel(
                      "companyMembers.tooltip.addNotes",
                      "Add Notes"
                    )}
                  >
                    <Button
                      sx={{
                        display: UserService.hasRole(["pc-tag-external-notes"])
                          ? "block"
                          : "none",
                      }}
                      color="primary"
                      onClick={(e) => onDetailButtonClick(e, params.row)}
                    >
                      <NoteAddIcon />
                    </Button>
                  </Tooltip>
                </>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Fragment>
      {isLoading || isLoadingDataCount || isLoadingLocations ? (
        <CircularProgress color="inherit" />
      ) : !isError ? (
        <div style={{ height: 450, width: "100%" }}>
          <div style={{ display: "flex" }}>
            <div style={{ marginRight: "20px" }}>
              <h1>{title}</h1>
            </div>
            {isFetching ? <RefreshingIndicator /> : ""}
          </div>
          <div
            style={{
              textAlign: "right",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <FormControl
              sx={{
                m: 1,
                width: 500,
                display: UserService.hasRole(["pc-lock-unlock-cards"])
                  ? "flex"
                  : "none",
                justifyContent: "flex-end",
              }}
            >
              <InputLabel id="demo-multiple-chip-label">
                {Labels.getLabel("companyMembers.locations", "Locations")}
              </InputLabel>
              <Select
                labelId="demo-multiple-chip-label"
                id="demo-multiple-chip"
                multiple
                value={elements}
                onChange={handleChangeElements}
                input={
                  <OutlinedInput
                    id="select-multiple-chip"
                    label="LocationsChip"
                  />
                }
                renderValue={(selected, other) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((key) => {
                      return (
                        <Chip
                          key={key}
                          label={
                            dataLocations?.data.find((item) => item.id === key)
                              .description
                          }
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {dataLocations?.data.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    style={getStyles(option.description, elements, theme)}
                  >
                    {option.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <DataGrid
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageSizeChange={onPageSizeChange}
            columnVisibilityModel={{ id: false }}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 800 },
              },
            }}
            rows={data?.data}
            columns={dataColumns}
            rowCount={dataCount?.data}
            onPageChange={(newPage) => {
              setPageFunction(newPage);
            }}
            paginationMode="server"
            filterMode="server"
            onFilterModelChange={onFilterChange}
            keepNonExistentRowsSelected
            getRowId={(row) => row.memberId + row.companyId}
            sx={{
              height: "calc(55vh)",
              "& .MuiButton-textSizeSmall ": { display: "none" },
              "& .MuiTypography-root": { fontFamily: "sans-serif" },
            }}
          />
          <Snackbar
            open={feedback.open}
            autoHideDuration={6000}
            onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={
                feedback.onClose ? feedback.onClose : handleFeedbackClose
              }
              severity={feedback.type}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <h4>
          {Labels.getLabel(
            "companyMembers.error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
      {openNewDlg && (
        <ManageCard
          open={openNewDlg}
          setOpen={setOpenNewDlg}
          dialogTitle={Labels.getLabel(
            "companyMembers.dialog.payAnyDayInfo",
            "PayAnyDay Info"
          )}
          memberInfo={memberInfo}
        />
      )}
      {openDetailDlg && (
        <MemberTransactions
          open={openDetailDlg}
          setOpen={setOpenDetailDlg}
          dialogTitle={Labels.getLabel(
            "companyMembers.dialog.memberInfo",
            "Member Info"
          )}
          memberInfo={memberInfo}
        />
      )}
      {openMemberInfoDlg && (
        <MemberDetail
          open={openMemberInfoDlg}
          setOpen={setOpenMemberInfoDlg}
          dialogTitle={Labels.getLabel(
            "companyMembers.dialog.viewMemberInfo",
            "PayAnyDay Info"
          )}
          onlyPADInfo={true}
          memberInfo={memberInfo}
          companyView={true}
        />
      )}
    </Fragment>
  );
}
